import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { canAccessSection, type SectionId } from '@shared/types';
import { useAuth } from './auth';
import { ThemeProvider } from './lib/theme';
import { VaultProvider } from './features/vault';
import { Layout } from './components/Layout';
import { VisitsPage } from './pages/VisitsPage';
import { TasksPage } from './pages/TasksPage';
import { ClientPage } from './pages/ClientPage';
import { BalanceDetailPage } from './features/client/BalanceDetailPage';
import { ClientDocumentsPage } from './features/client/ClientDocumentsPage';
import { VaultPage } from './pages/VaultPage';
import { MorePage } from './pages/MorePage';
import { NavigationPage } from './features/navigation';
import { RoutePlannerPage } from './features/route-planner/RoutePlannerPage';
import { RouteMapPage } from './pages/RouteMapPage';
import { GpsDiagnosticsPage } from './features/route-planner/GpsDiagnosticsPage';
import { useLocationAutoSave } from './features/route-planner/hooks/useLocationAutoSave';
import { CablePage, CableUsagePage, WarehousePage, RemontPage } from './features/legacy';
import { KbasePage } from './features/kbase/KbasePage';
import { ISPStatusPage } from './features/isp/ISPStatusPage';
import { StationsControlPage, StationDetailPage } from './features/stations';
import { IpEzByPage } from './features/ipezby';
import { FuelPage } from './features/fuel/FuelPage';
import { WaybillsPage } from './features/waybills';
import { ProfilePage } from './pages/ProfilePage';
import { DepartedClientsPage } from './pages/DepartedClientsPage';
import LlmPage from './features/llm/LlmPage';
import LlmChatPage from './features/llm/LlmChatPage';
import { DoorPage } from './features/door/DoorPage';
import { FileSharePage } from './pages/FileSharePage';
import { InstallersPage, LicensingPage, LicensingStationDetailPage } from './features/licensing';
import { UsefulContactsPage } from './features/useful-contacts/UsefulContactsPage';
import { SubcontractorWorksPage } from './features/subcontractor-works/SubcontractorWorksPage';
import { PersonalNotesPage } from './features/personal-notes/PersonalNotesPage';
import { ActionItemsPage } from './features/action-items/ActionItemsPage';
import { ControlledDocumentsPage } from './features/controlled-documents/ControlledDocumentsPage';
import { EmployeePersonalDataPage } from './features/employee-personal-data/EmployeePersonalDataPage';
import { EmployeePromptHost } from './features/employee-prompts/EmployeePromptHost';
import { CompanyRequisitesPage } from './features/company-requisites/CompanyRequisitesPage';

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

// Global GPS location tracker - saves location when app opens or tab becomes visible
function LocationTracker() {
  useLocationAutoSave();
  return null;
}

function RequireSectionAccess({
  section,
  children,
}: {
  section: SectionId;
  children: ReactNode;
}) {
  const { user } = useAuth();

  if (!user) {
    return <LoadingScreen />;
  }

  if (!canAccessSection(user.roles, section)) {
    return <Navigate to="/more" replace />;
  }

  return children;
}

export function App() {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground">Redirecting to login...</p>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <VaultProvider>
        <LocationTracker />
        <Toaster position="bottom-center" richColors closeButton />
        <BrowserRouter>
          <EmployeePromptHost />
          <Routes>
            {/* Fullscreen pages - outside Layout */}
            <Route path="navigation" element={<NavigationPage />} />
            <Route path="more/chat/:conversationId" element={<LlmChatPage />} />

            {/* Regular routes with Layout */}
            <Route element={<Layout />}>
              <Route index element={<Navigate to="/visits" replace />} />
              <Route path="visits" element={<VisitsPage />} />
              <Route path="visits/client/:clientId" element={<ClientPage />} />
              <Route path="visits/client/:clientId/balance/:legalEntity" element={<BalanceDetailPage />} />
              <Route path="visits/client/:clientId/documents" element={<ClientDocumentsPage />} />
              <Route path="visits/route" element={<RouteMapPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="vault" element={<VaultPage />} />
              <Route path="more" element={<MorePage />} />
              <Route
                path="more/requisites"
                element={
                  <RequireSectionAccess section="settings/requisites">
                    <CompanyRequisitesPage />
                  </RequireSectionAccess>
                }
              />
              <Route path="more/cable" element={<CablePage />} />
              <Route path="more/cable/usage/:clientName?" element={<CableUsagePage />} />
              <Route path="more/warehouse" element={<WarehousePage />} />
              <Route path="more/isp-status" element={<ISPStatusPage />} />
              <Route path="more/stations-control" element={<StationsControlPage />} />
              <Route path="more/stations-control/station" element={<StationDetailPage />} />
              <Route path="more/ip-ez-by" element={<IpEzByPage />} />
              <Route path="more/fuel" element={<FuelPage />} />
              <Route path="more/kbase" element={<KbasePage />} />
              <Route path="more/remont" element={<RemontPage />} />
              <Route path="more/subcontractor-works" element={<SubcontractorWorksPage />} />
              <Route path="more/useful-contacts" element={<UsefulContactsPage />} />
              <Route path="more/personal-notes" element={<PersonalNotesPage />} />
              <Route path="more/controlled-documents" element={<ControlledDocumentsPage />} />
              <Route path="more/action-items" element={<ActionItemsPage />} />
              <Route path="more/personal-data" element={<EmployeePersonalDataPage />} />
              <Route path="more/departed-clients" element={<DepartedClientsPage />} />
              <Route path="more/waybills" element={<WaybillsPage />} />
              <Route path="more/door" element={<DoorPage />} />
              <Route path="more/chat" element={<LlmPage />} />
              <Route path="more/file-share" element={<FileSharePage />} />
              <Route path="more/licensing" element={<LicensingPage />} />
              <Route path="more/installers" element={<InstallersPage />} />
              <Route path="more/licensing/stations/:stationId" element={<LicensingStationDetailPage />} />
              <Route path="route-planner" element={<RoutePlannerPage />} />
              <Route path="route-planner/gps-diagnostics" element={<GpsDiagnosticsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </VaultProvider>
    </ThemeProvider>
  );
}
